import type {
  GetAgencyMembers200DataItemsItem,
  GetAgencyMembersAgencyMemberIdActivity200DataItemsItem,
  GetAgencyMembersAgencyMemberIdActivity200DataItemsItemKind,
  GetAgencyOrganizations200DataItemsItem,
  GetAgencyOrganizations200DataItemsItemAssignedMembersItem,
} from '@/api/generated/schemas';

export type AgencyMember = GetAgencyMembers200DataItemsItem;
/**
 * A member's own record without the salon-access list — what `/agency/me`
 * returns. Profile / header / account UI reads only these fields, so the
 * self-profile page can render them for a viewer who can't list members.
 */
export type AgencyMemberRecord = Omit<AgencyMember, 'organizationAccess'>;
export type AgencyOrganization = GetAgencyOrganizations200DataItemsItem;
/** Compact member summary the org list embeds for the salon-card team stack. */
export type AgencyAssignedMember = GetAgencyOrganizations200DataItemsItemAssignedMembersItem;
/** One row of a member's activity timeline (`GET /agency/members/{id}/activity`). */
export type AgencyMemberActivityItem = GetAgencyMembersAgencyMemberIdActivity200DataItemsItem;
export type AgencyMemberActivityKind = GetAgencyMembersAgencyMemberIdActivity200DataItemsItemKind;

/** Anything carrying a member's name parts + email (full member, activity actor). */
type MemberNameSource = Pick<AgencyMember, 'firstName' | 'lastName' | 'email'>;

export function getAgencyMemberName(member: MemberNameSource): string {
  const name = [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
  return name || member.email;
}

/**
 * First name for conversational copy ("Andy will show as Active…"). Falls back
 * to the full display name (which itself falls back to the email).
 */
export function getAgencyMemberFirstName(member: MemberNameSource): string {
  return member.firstName?.trim() || getAgencyMemberName(member);
}

/** Salon display name; falls back to the short code when no business name is set. */
export function getOrganizationName(organization: {
  businessName?: string | null;
  shortCode: string;
}): string {
  return organization.businessName?.trim() || `Salon ${organization.shortCode}`;
}

export function getOrganizationLocation(organization: AgencyOrganization): string {
  const location = organization.primaryLocation;
  if (!location) return 'Location not configured';

  return [location.name, location.city, location.state].filter(Boolean).join(' · ');
}

/**
 * Format the primary location's street address into a single human line
 * ("128 Spring Street, New York, NY 10012"). Returns null when no address is
 * configured so the card can hide the row entirely.
 */
export function getOrganizationAddress(organization: Pick<AgencyOrganization, 'primaryLocation'>): string | null {
  const location = organization.primaryLocation;
  if (!location) return null;

  const street = [location.addressLine1, location.addressLine2].filter(Boolean).join(', ');
  const cityState = [location.city, location.state].filter(Boolean).join(', ');
  const regionLine = [cityState, location.zipCode].filter(Boolean).join(' ');

  const address = [street, regionLine].filter(Boolean).join(', ').trim();
  return address || null;
}

/**
 * Two-line location for the salons list: "Austin, TX" headline plus the
 * street line. Falls back to the street alone when city/state are missing.
 */
export function getOrganizationLocationLines(
  organization: AgencyOrganization
): { primary: string; secondary: string | null } | null {
  const location = organization.primaryLocation;
  if (!location) return null;
  const cityState = [location.city, location.state].filter(Boolean).join(', ');
  const street = [location.addressLine1, location.addressLine2].filter(Boolean).join(', ');
  if (cityState) return { primary: cityState, secondary: street || null };
  return street ? { primary: street, secondary: null } : null;
}

/** Salons page orders (the API sorts: `sort` on `GET /agency/organizations`). Created date or name. */
export type AgencySalonSort = 'newest' | 'oldest' | 'name-asc' | 'name-desc';

/**
 * Salons page status filter (`status` on `GET /agency/organizations`): canceled = the subscription's CANCELED status,
 * every other salon (also one with no subscription) is active. The page opens on active.
 */
export type AgencySalonStatusFilter = 'active' | 'canceled';

export const AGENCY_SALON_STATUS_FILTER_LABEL: Record<AgencySalonStatusFilter, string> = {
  active: 'Active',
  canceled: 'Canceled',
};

export const AGENCY_SALON_SORT_LABEL: Record<AgencySalonSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  'name-asc': 'Name A–Z',
  'name-desc': 'Name Z–A',
};

export {
  formatLastActive,
  formatMemberLastActive,
  formatMonthDay,
  formatRelativeDate,
  formatShortDate,
} from './agency-format';

/** HTTP status of a failed API call (axios error), or undefined for a network / non-HTTP failure. */
export function getAgencyHttpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/**
 * React Query `retry` for agency reads: a 4xx (not found, no access) is an answer, so show it at once; network
 * failures and 5xx get two quick retries.
 */
export function retryAgencyReadUnlessClientError(failureCount: number, error: unknown): boolean {
  const status = getAgencyHttpStatus(error);
  if (status !== undefined && status < 500) return false;
  return failureCount < 2;
}

/**
 * Human message from an API/axios error. The API answers in three shapes:
 * `{ message }`, `{ error: 'text' }` and `{ error: { message } }` (coded
 * errors); zod body errors (`{ error: { issues } }`) carry no message, so the
 * fallback is used rather than leaking an object into a toast. axios's generic
 * "Request failed with status code 400" is skipped for the same reason; a
 * plain thrown `Error` (no response) keeps its own message.
 */
export function getAgencyErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as {
    response?: { data?: { message?: unknown; error?: unknown } };
    message?: unknown;
  };
  const data = candidate.response?.data;
  const nested = data?.error as { message?: unknown } | undefined;

  const ownMessage = candidate.response ? undefined : candidate.message;

  const message = [data?.message, data?.error, nested?.message, ownMessage].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0
  );
  return message ?? fallback;
}
